import React, { useCallback, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { Text } from '@common-fe/common-fe';

import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';

import { FraudFlagButton, FraudReviewBanner, FraudReviewModal } from './components';
import { FraudReviewModalKind } from './fraudReview.constants';
import { useFraudReviewPermissions } from './hooks';
import {
  useConfirmFraudQuery,
  useFraudReviewStatusQuery,
  useUpdateFraudReviewQuery,
} from './queries';

const ERROR_SNACKBAR_TEXT = 'Something went wrong. Please try again later.';
const SNACKBAR_WIDTH = '320px';
const SNACKBAR_HEIGHT = '56px';

const BoldName: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text as="span" weight={700}>
    {children}
  </Text>
);

interface Props {
  placement: 'topbar' | 'content';
  employeeName: string;
}

const FraudReviewContainer: React.FC<Props> = ({ placement, employeeName }) => {
  const {
    params: { id: employeeId },
  } = useRouteMatch<{ id: string }>();
  const { isFeatureAvailable, canManageFlag } = useFraudReviewPermissions();
  const [modalKind, setModalKind] = useState<FraudReviewModalKind | null>(null);

  const { isFraudReviewRequired, isLoading } = useFraudReviewStatusQuery(
    employeeId,
    isFeatureAvailable
  );
  const { handleAddPermanentSnackbar } = useSnackbar();

  const closeModal = useCallback(() => setModalKind(null), []);

  const showSnackbar = useCallback(
    (text: React.ReactNode, state: SnackbarState) => {
      handleAddPermanentSnackbar({
        text,
        state,
        closeIcon: true,
        autoRemove: true,
        width: SNACKBAR_WIDTH,
        height: SNACKBAR_HEIGHT,
      });
    },
    [handleAddPermanentSnackbar]
  );

  const { updateFraudReview, isPending: isUpdating } = useUpdateFraudReviewQuery(
    employeeId,
    closeModal
  );
  const { confirmFraud, isPending: isConfirming } = useConfirmFraudQuery(employeeId, closeModal);

  const runWithSnackbar = useCallback(
    async (mutate: () => Promise<unknown>, successText: React.ReactNode, successState: SnackbarState) => {
      try {
        await mutate();
        showSnackbar(successText, successState);
      } catch {
        showSnackbar(ERROR_SNACKBAR_TEXT, SnackbarState.negative);
      }
    },
    [showSnackbar]
  );

  const handleConfirm = useCallback(() => {
    const name = employeeName || 'Employee';
    switch (modalKind) {
      case FraudReviewModalKind.Flag:
        return runWithSnackbar(
          () => updateFraudReview(true),
          <><BoldName>{name}</BoldName> has been flagged for fraud</>,
          SnackbarState.negative
        );
      case FraudReviewModalKind.Remove:
        return runWithSnackbar(
          () => updateFraudReview(false),
          <>Fraud flag has been removed from <BoldName>{name}</BoldName></>,
          SnackbarState.positive
        );
      case FraudReviewModalKind.Confirm:
        return runWithSnackbar(
          () => confirmFraud(),
          `Fraud confirmed. ${name} must re-register.`,
          SnackbarState.positive
        );
      default:
        return undefined;
    }
  }, [modalKind, employeeName, runWithSnackbar, updateFraudReview, confirmFraud]);

  if (!isFeatureAvailable || isLoading) {
    return null;
  }

  const modal = (
    <FraudReviewModal
      kind={modalKind}
      employeeName={employeeName}
      isSubmitting={isUpdating || isConfirming}
      onConfirm={handleConfirm}
      onClose={closeModal}
    />
  );

  if (placement === 'topbar') {
    if (isFraudReviewRequired || !canManageFlag) {
      return null;
    }
    return (
      <>
        <FraudFlagButton onClick={() => setModalKind(FraudReviewModalKind.Flag)} />
        {modal}
      </>
    );
  }

  if (!isFraudReviewRequired) {
    return null;
  }

  return (
    <>
      <FraudReviewBanner
        canManageFlag={canManageFlag}
        onRemoveFlag={() => setModalKind(FraudReviewModalKind.Remove)}
        onConfirmFraud={() => setModalKind(FraudReviewModalKind.Confirm)}
      />
      {modal}
    </>
  );
};

export default FraudReviewContainer;
